import * as React from 'react';
import { LeadInsuranceDetails } from '../../../../types';
import Section from '@hero/ui-kit/layout/Section';
import Container from '@hero/ui-kit/layout/Container';
import useIsMinBreakpoint from '@hero/react-hooks/mediaQuery/useIsMinBreakpoint';
import AlertIcon from '@hero/ui-kit/icons/utility/AlertIcon';
import P from '@hero/ui-kit/typography/P';
import Label from '@hero/ui-kit/typography/Label';

interface InsuranceDiscoveryProps {
    insuranceDiscovery: LeadInsuranceDetails | null;
}

const InsuranceDiscoveryDetails: React.FC<{
    details: { label: string; value: string }[];
}> = ({ details }) => {
    const isMinL = useIsMinBreakpoint('l');

    const defaultColumns = isMinL ? '1fr 1fr' : '1fr';

    const columns = details.length === 1 ? '1fr' : defaultColumns;

    return (
        <Container gridTemplateColumns={columns} gridColumnGap="regular">
            {details.map((detail) => (
                <div key={detail.label}>
                    <Label htmlFor={detail.label}>{detail.label}</Label>
                    <P noDefaultMargin>{detail.value || '-'}</P>
                </div>
            ))}
        </Container>
    );
};

const InsuranceDiscovery: React.FC<InsuranceDiscoveryProps> = ({ insuranceDiscovery }) => {
    const address = `${insuranceDiscovery?.address || '-'}, ${insuranceDiscovery?.city || '-'}, ${
        insuranceDiscovery?.state || '-'
    } ${insuranceDiscovery?.zip || '-'}`;

    return (
        <Section noDefaultPadding styles={{ marginBottom: 'var(--gutter)' }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    columnGap: '2rem',
                    padding: '16px',
                    borderRadius: '8px',
                    backgroundColor: '#f4f4f4',
                    marginBottom: '3.6rem'
                }}
            >
                <AlertIcon size="utility" />
                <P noDefaultMargin size="small">
                    The health insurance details below were received from a third-party source and
                    are being shared by Hero for convenience. It’s the provider’s responsibility to
                    verify their accuracy.
                </P>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', rowGap: '3.6rem' }}>
                <InsuranceDiscoveryDetails
                    details={[
                        {
                            label: 'NAME',
                            value: insuranceDiscovery?.name || '-'
                        },
                        {
                            label: 'DATE OF BIRTH',
                            value: insuranceDiscovery?.date_of_birth || '-'
                        }
                    ]}
                />
                <InsuranceDiscoveryDetails
                    details={[
                        {
                            label: 'GENDER',
                            value: insuranceDiscovery?.gender || '-'
                        },
                        {
                            label: 'ADDRESS',
                            value: address
                        }
                    ]}
                />
                <InsuranceDiscoveryDetails
                    details={[
                        {
                            label: 'INSURANCE POLICY TYPE',
                            value: insuranceDiscovery?.policy_type || '-'
                        },
                        {
                            label: 'PAYER',
                            value: insuranceDiscovery?.payer || '-'
                        }
                    ]}
                />
                <InsuranceDiscoveryDetails
                    details={[
                        {
                            label: 'MEMBER ID',
                            value: insuranceDiscovery?.member_id || '-'
                        },
                        {
                            label: 'EFFECTIVE FROM DATE',
                            value: insuranceDiscovery?.effective_date || '-'
                        }
                    ]}
                />
                <InsuranceDiscoveryDetails
                    details={[
                        {
                            label: 'PLAN NAME',
                            value: insuranceDiscovery?.plan_name || '-'
                        }
                    ]}
                />
                <InsuranceDiscoveryDetails
                    details={[
                        {
                            label: 'PLAN NUMBER',
                            value: insuranceDiscovery?.plan_number || '-'
                        }
                    ]}
                />
                <InsuranceDiscoveryDetails
                    details={[
                        {
                            label: 'GROUP NUMBER',
                            value: insuranceDiscovery?.group_number || '-'
                        },
                        {
                            label: 'PLAN NETWORK ID',
                            value: insuranceDiscovery?.plan_network_id || '-'
                        }
                    ]}
                />
                <InsuranceDiscoveryDetails
                    details={[
                        {
                            label: 'GROUP NAME',
                            value: insuranceDiscovery?.group_name || '-'
                        },
                        {
                            label: 'PLAN NETWORK NAME',
                            value: insuranceDiscovery?.plan_network_name || '-'
                        }
                    ]}
                />
            </div>
        </Section>
    );
};

export default InsuranceDiscovery;
